import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { gridContainerVariants, getGridItemVariants } from '@/lib/animations';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useReducedMotion } from '@/hooks/useReducedMotion';

type BrandLevel = 'silver' | 'gold' | 'platinum';
type WebsiteScope = 'landing' | 'three-page' | 'full';
type Support = 'launch-only' | 'launch-advisory';

interface OpsState {
  llc: boolean;
  contract: boolean;
  hr: boolean;
}

interface Ranges {
  timeline: [number, number];
  investment: [number, number];
}

function calculateRanges(
  brandLevel: BrandLevel,
  websiteScope: WebsiteScope,
  ops: OpsState,
  support: Support
): Ranges {
  // Base timeline ranges (weeks)
  const baseTimeline: Record<BrandLevel, [number, number]> = {
    silver: [4, 6],
    gold: [5, 7],
    platinum: [6, 8],
  };

  // Website scope additions (weeks)
  const websiteAdditions: Record<WebsiteScope, [number, number]> = {
    landing: [0, 0],
    'three-page': [1, 1],
    full: [2, 2],
  };

  // Ops additions (weeks per item)
  const opsCount = [ops.llc, ops.contract, ops.hr].filter(Boolean).length;
  const opsAddition = opsCount * 0.75; // ~0.75 weeks per ops item

  // Support additions (weeks)
  const supportAddition: Record<Support, [number, number]> = {
    'launch-only': [0, 0],
    'launch-advisory': [2, 4],
  };

  // Calculate timeline
  const [baseMin, baseMax] = baseTimeline[brandLevel];
  const [websiteMin, websiteMax] = websiteAdditions[websiteScope];
  const [supportMin, supportMax] = supportAddition[support];
  const timelineMin = Math.round(baseMin + websiteMin + opsAddition + supportMin);
  const timelineMax = Math.round(baseMax + websiteMax + opsAddition + supportMax);

  // Base investment ranges (dollars in thousands)
  const baseInvestment: Record<BrandLevel, [number, number]> = {
    silver: [5, 8],
    gold: [8, 12],
    platinum: [12, 18],
  };

  // Website scope additions (dollars in thousands)
  const websiteInvestment: Record<WebsiteScope, [number, number]> = {
    landing: [0, 2],
    'three-page': [2, 4],
    full: [5, 10],
  };

  // Ops additions (dollars in thousands per item)
  const opsInvestment = opsCount * 1.5; // ~$1.5k per ops item

  // Support additions (dollars in thousands)
  const supportInvestment: Record<Support, [number, number]> = {
    'launch-only': [0, 0],
    'launch-advisory': [3, 8],
  };

  // Calculate investment
  const [invBaseMin, invBaseMax] = baseInvestment[brandLevel];
  const [invWebsiteMin, invWebsiteMax] = websiteInvestment[websiteScope];
  const [invSupportMin, invSupportMax] = supportInvestment[support];
  const investmentMin = Math.round(invBaseMin + invWebsiteMin + opsInvestment + invSupportMin);
  const investmentMax = Math.round(invBaseMax + invWebsiteMax + opsInvestment + invSupportMax);

  return {
    timeline: [timelineMin, timelineMax],
    investment: [investmentMin, investmentMax],
  };
}

const BRAND_LEVELS: Array<{ value: BrandLevel; label: string; description: string }> = [
  {
    value: 'silver',
    label: 'Silver',
    description: 'Premium domain and essential visual identity.',
  },
  {
    value: 'gold',
    label: 'Gold',
    description: 'Stronger name options plus a more developed brand system.',
  },
  {
    value: 'platinum',
    label: 'Platinum',
    description: 'Category-owning domain with full brand strategy and assets.',
  },
];

const WEBSITE_SCOPES: Array<{ value: WebsiteScope; label: string; description: string }> = [
  {
    value: 'landing',
    label: 'Landing page',
    description: 'Single, sharp page to book consults.',
  },
  {
    value: 'three-page',
    label: '3-page site',
    description: 'Home, services, and about—enough to feel complete.',
  },
  {
    value: 'full',
    label: 'Full build',
    description: 'Custom architecture for multiple offers, content, and lead flows.',
  },
];

const SUPPORT_OPTIONS: Array<{ value: Support; label: string; description: string }> = [
  {
    value: 'launch-only',
    label: 'Launch only',
    description: 'Get it launched, then you run it.',
  },
  {
    value: 'launch-advisory',
    label: 'Launch + advisory',
    description: 'Stay on for monthly refinement and strategy.',
  },
];

export function HomeBusinessBuilder() {
  const [brandLevel, setBrandLevel] = useState<BrandLevel>('gold');
  const [websiteScope, setWebsiteScope] = useState<WebsiteScope>('three-page');
  const [ops, setOps] = useState<OpsState>({
    llc: false,
    contract: true,
    hr: false,
  });
  const [support, setSupport] = useState<Support>('launch-only');
  const prefersReducedMotion = useReducedMotion();

  const ranges = useMemo(
    () => calculateRanges(brandLevel, websiteScope, ops, support),
    [brandLevel, websiteScope, ops, support]
  );

  const opsList = useMemo(() => {
    const items: string[] = [];
    if (ops.llc) items.push('LLC / entity setup');
    if (ops.contract) items.push('Contract & legal starter kit');
    if (ops.hr) items.push('HR / people-ops starter kit');
    return items.length > 0 ? items.join(', ') : 'None chosen yet';
  }, [ops]);

  const headingReveal = useRevealOnce({ amount: 0.25 });
  const formReveal = useRevealOnce({ amount: 0.25 });

  return (
    <section id="business-builder" className="bg-white py-16 md:py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Heading and intro */}
          <motion.div
            ref={headingReveal.ref}
            className="text-center mb-12 md:mb-16"
            variants={gridContainerVariants}
            initial="hidden"
            animate={headingReveal.controls}
          >
            <motion.h2
              variants={getGridItemVariants(0)}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 md:mb-8 text-slate-900"
            >
              See what a proper launch would actually take.
            </motion.h2>
            <motion.p
              variants={getGridItemVariants(1)}
              className="text-lg md:text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed"
            >
              Use this free planner to sketch out your domain-plus-launch setup. In a minute or two, you'll see a realistic range for investment and timeline—based on real projects, not wishful thinking.
            </motion.p>
          </motion.div>

          {/* Two-column layout: inputs and summary */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Inputs column (3 cols on desktop) */}
            <motion.div
              ref={formReveal.ref}
              className="lg:col-span-3 space-y-8"
              variants={gridContainerVariants}
              initial="hidden"
              animate={formReveal.controls}
            >
              {/* Input Group 1 – Domain & brand level */}
              <motion.div variants={getGridItemVariants(0)} className="space-y-4">
                <div>
                  <Label className="text-base font-semibold text-slate-900">Domain & brand level</Label>
                  <p className="text-sm text-slate-600 mt-1">Choose how far you want to go on naming and brand identity.</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {BRAND_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setBrandLevel(level.value)}
                      className={`flex-1 min-w-[140px] px-4 py-3 rounded-full text-sm font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                        brandLevel === level.value
                          ? 'bg-[#0000FF] text-white border-2 border-[#0000FF]'
                          : 'bg-slate-100 text-slate-700 border-2 border-slate-300 hover:border-slate-400'
                      }`}
                      aria-pressed={brandLevel === level.value}
                      role="button"
                    >
                      <div className="font-bold">{level.label}</div>
                      <div className="text-xs mt-1 opacity-90">{level.description}</div>
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* Input Group 2 – Website scope */}
              <motion.div variants={getGridItemVariants(1)} className="space-y-4">
                <div>
                  <Label className="text-base font-semibold text-slate-900">Website scope</Label>
                  <p className="text-sm text-slate-600 mt-1">How big does your site need to be for the offers you sell?</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {WEBSITE_SCOPES.map((scope) => (
                    <button
                      key={scope.value}
                      onClick={() => setWebsiteScope(scope.value)}
                      className={`flex-1 min-w-[140px] px-4 py-3 rounded-full text-sm font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                        websiteScope === scope.value
                          ? 'bg-[#0000FF] text-white border-2 border-[#0000FF]'
                          : 'bg-slate-100 text-slate-700 border-2 border-slate-300 hover:border-slate-400'
                      }`}
                      aria-pressed={websiteScope === scope.value}
                      role="button"
                    >
                      <div className="font-bold">{scope.label}</div>
                      <div className="text-xs mt-1 opacity-90">{scope.description}</div>
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* Input Group 3 – Operational basics */}
              <motion.div variants={getGridItemVariants(2)} className="space-y-4">
                <div>
                  <Label className="text-base font-semibold text-slate-900">Operational basics</Label>
                  <p className="text-sm text-slate-600 mt-1">Add the essentials so you don't feel DIY when real clients show up.</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="ops-llc"
                      checked={ops.llc}
                      onCheckedChange={(checked) => setOps((prev) => ({ ...prev, llc: checked === true }))}
                    />
                    <Label htmlFor="ops-llc" className="text-sm font-medium text-slate-700 cursor-pointer">
                      LLC / entity setup
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="ops-contract"
                      checked={ops.contract}
                      onCheckedChange={(checked) => setOps((prev) => ({ ...prev, contract: checked === true }))}
                    />
                    <Label htmlFor="ops-contract" className="text-sm font-medium text-slate-700 cursor-pointer">
                      Contract & legal starter kit
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="ops-hr"
                      checked={ops.hr}
                      onCheckedChange={(checked) => setOps((prev) => ({ ...prev, hr: checked === true }))}
                    />
                    <Label htmlFor="ops-hr" className="text-sm font-medium text-slate-700 cursor-pointer">
                      HR / people-ops starter kit
                    </Label>
                  </div>
                </div>
              </motion.div>

              {/* Input Group 4 – Ongoing support */}
              <motion.div variants={getGridItemVariants(3)} className="space-y-4">
                <div>
                  <Label className="text-base font-semibold text-slate-900">Ongoing support</Label>
                  <p className="text-sm text-slate-600 mt-1">Decide how much help you want after launch.</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {SUPPORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSupport(option.value)}
                      className={`flex-1 min-w-[140px] px-4 py-3 rounded-full text-sm font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                        support === option.value
                          ? 'bg-[#0000FF] text-white border-2 border-[#0000FF]'
                          : 'bg-slate-100 text-slate-700 border-2 border-slate-300 hover:border-slate-400'
                      }`}
                      aria-pressed={support === option.value}
                      role="button"
                    >
                      <div className="font-bold">{option.label}</div>
                      <div className="text-xs mt-1 opacity-90">{option.description}</div>
                    </button>
                  ))}
                </div>
              </motion.div>
            </motion.div>

            {/* Summary card column (2 cols on desktop) */}
            <motion.div
              className="lg:col-span-2"
              variants={gridContainerVariants}
              initial="hidden"
              animate={formReveal.controls}
            >
              <motion.div variants={getGridItemVariants(4)}>
                <Card className="shadow-md border border-slate-200 hover:border-blue-300 transition-colors duration-300 sticky top-8">
                  <CardHeader>
                    <CardTitle className="text-2xl font-bold text-slate-900">Your launch at a glance</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6" aria-live="polite">
                    <p className="text-base text-slate-700 leading-relaxed">
                      Based on what you've chosen, most clients launch in{' '}
                      <AnimatePresence mode="wait">
                        {!prefersReducedMotion ? (
                          <motion.span
                            key={`${ranges.timeline[0]}-${ranges.timeline[1]}`}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                            className="inline-block text-xl font-bold text-[#0000FF]"
                          >
                            {ranges.timeline[0]}–{ranges.timeline[1]} weeks
                          </motion.span>
                        ) : (
                          <span className="inline-block text-xl font-bold text-[#0000FF]">
                            {ranges.timeline[0]}–{ranges.timeline[1]} weeks
                          </span>
                        )}
                      </AnimatePresence>{' '}
                      with a total investment in the{' '}
                      <AnimatePresence mode="wait">
                        {!prefersReducedMotion ? (
                          <motion.span
                            key={`${ranges.investment[0]}-${ranges.investment[1]}`}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                            className="inline-block text-xl font-bold text-[#0000FF]"
                          >
                            ${ranges.investment[0]}k–${ranges.investment[1]}k
                          </motion.span>
                        ) : (
                          <span className="inline-block text-xl font-bold text-[#0000FF]">
                            ${ranges.investment[0]}k–${ranges.investment[1]}k
                          </span>
                        )}
                      </AnimatePresence>{' '}
                      range.
                    </p>

                    <ul className="space-y-2 text-sm text-slate-600">
                      <li>
                        <span className="font-semibold">Domain & brand:</span>{' '}
                        {BRAND_LEVELS.find((l) => l.value === brandLevel)?.label}
                      </li>
                      <li>
                        <span className="font-semibold">Website:</span>{' '}
                        {WEBSITE_SCOPES.find((s) => s.value === websiteScope)?.label}
                      </li>
                      <li>
                        <span className="font-semibold">Operations:</span> {opsList}
                      </li>
                      <li>
                        <span className="font-semibold">Support:</span>{' '}
                        {SUPPORT_OPTIONS.find((o) => o.value === support)?.label}
                      </li>
                    </ul>

                    <p className="text-xs text-slate-500 italic leading-relaxed">
                      Skipping the ops and launch side usually costs more in lost deals than it saves in budget.
                    </p>

                    <Link to="/services">
                      <Button className="w-full bg-[#0000FF] hover:bg-[#0000EE] text-white" size="lg">
                        Request this launch plan
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

